import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import { ScrollView, TouchableOpacity } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { ErrorMessagePayment } from '@/components/ErrorMessagePayment';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { NumberPad } from '@/components/NumberPad';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { URLS } from '@/data';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PurchaseSummaryCard } from '@/feature/deposit/components/PurchaseSummaryCard';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { useDepositMatch } from '@/feature/promohub/hooks/use-deposit-match';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import { hasMadeFirstDepositSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useCurrencyNumberPad } from '@/hooks/use-currency-number-pad';
import { useIsFirstScreenInStack } from '@/hooks/use-is-first-screen-in-stack';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { DepositScreenNavigationProp } from '@/navigation/deposit/types';
import { DepositStackParamList } from '@/navigation/types';
import { common } from '@/styles/styles';
import { standardValue } from '@/utils/constants';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { DepositButton } from '../components/DepositButton';
import { DepositNumberSection } from '../components/DepositNumberSection';
import { PaymentMethods } from '../components/PaymentMethods';
import { PromotionalBanner } from '../components/PromotionalBanner';
import { QuickDepositButtonsSection } from '../components/QuickDepositButtonsSection';
import { PaymentMethodTypes } from '../const';
import { useDepositAmountValidation } from '../hooks/use-deposit-amount-validation';
import { PaymentMethod, PreferredPayment } from '../hooks/use-deposit-payment-methods';
import { setDepositAmount, useDepositStatusStore } from '../hooks/use-deposit-status-store';
import { useUserSignupPromoDetail } from '../hooks/use-get-promo-codes';
import { usePrefilledAmounts } from '../hooks/use-prefilled-amounts';

const styles = StyleSheet.create({
    loaderContainer: { backgroundColor: ds.colorValue.background.neutral.base },
});

export const CTA_BUTTON_HEIGHT = 76;

type ScreenProps = NativeStackScreenProps<DepositStackParamList, 'DepositScreen'>;

export const DepositScreen = ({ route }: ScreenProps) => {
    const { isSharedSocialWallet } = useActiveWallet();
    const selectedAmount = useDepositStatusStore(state => state.selectedAmount);
    const coinPackage = useDepositStatusStore(state => state.coinPackage);
    const navigation = useNavigation<DepositScreenNavigationProp>();

    const isFirstScreenInStack = useIsFirstScreenInStack();
    const { t } = useTranslation(['wallet', 'common']);
    const suggestedRetryAmount = route.params?.suggestedRetryAmount;

    const product = useJurisdictionStore(state => state.product);
    const globalSettings = useJurisdictionStore(state => state.jurisdictionSettings?.globalSettings);
    const productConfig = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig);
    const settings = globalSettings?.keyValuePairs;
    const ftdSetting = productConfig?.ftdSetting;

    const { data: hasMadeFirstDeposit } = useAuthUserConfig({
        select: hasMadeFirstDepositSelector,
    });

    const [isEditing, setIsEditing] = useState(!isSharedSocialWallet);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>();
    const [selectedPreferredMethod, setSelectedPreferredMethod] = useState<PreferredPayment>();
    const [numPadHeight, setNumPadHeight] = useState<number>(0);
    const { bottom: safeAreaInsetBottom } = useSafeAreaInsets();
    const BONUS_ENGINE_V2_ENABLED = useJurisdictionStore(
        state => state.jurisdictionSettings?.globalSettings?.featureFlags?.bonus_engine_v2_enabled?.enabled
    );
    const isOnboarding = Boolean(!hasMadeFirstDeposit && route?.params?.isRedirectedFromVerificationSuccess);
    const shouldShowFTDBanner = hasMadeFirstDeposit !== undefined && !hasMadeFirstDeposit && !!ftdSetting;

    // when the user is redirected from the promo page to the deposit page
    // we receive the promoSlug to which he just opted in and we should match that one
    const { promotionToActivate } = useDepositMatch(
        route.params?.promoSlug,
        BONUS_ENGINE_V2_ENABLED && !shouldShowFTDBanner
    );
    const showPromotionalBanner = isEditing && (shouldShowFTDBanner ? shouldShowFTDBanner : !!promotionToActivate);

    const onLayout = (event: LayoutChangeEvent): void => {
        const { height } = event.nativeEvent.layout;

        if (height > numPadHeight) {
            setNumPadHeight(height + safeAreaInsetBottom + CTA_BUTTON_HEIGHT);
        }
    };

    const prefilledAmount = usePrefilledAmounts();
    const { displayValue, number, onNumberPress, onDecimalPress, onBackspacePress, setInputValue } =
        useCurrencyNumberPad(Number(selectedAmount));

    useEffect(() => {
        if (prefilledAmount && !isSharedSocialWallet) {
            setDepositAmount(prefilledAmount);
            setInputValue(prefilledAmount);
        }
    }, [setInputValue, prefilledAmount, isSharedSocialWallet]);

    useEffect(() => {
        if (suggestedRetryAmount) {
            const formattedAmount = toLocaleCurrency(suggestedRetryAmount).replace(/^\$/, '');
            setDepositAmount(formattedAmount);
            setInputValue(formattedAmount);
        }
    }, [setInputValue, suggestedRetryAmount]);

    const { errorMessage, isDepositLimitsLoading } = useDepositAmountValidation({
        amount: number,
        selectedPaymentMethodType: selectedPaymentMethod?.type,
    });
    const promoCodeDetail = useUserSignupPromoDetail();

    const getAnalyticsEvent = (method: PaymentMethod) => {
        switch (method.type) {
            case PaymentMethodTypes.Paysafe:
            case PaymentMethodTypes.Paysafe3ds:
                return AnalyticsEvent.DEPOSIT_METHOD_SAVED_DEBIT;
            case PaymentMethodTypes.PaysafeMazooma:
                return AnalyticsEvent.DEPOSIT_METHOD_SAVED_IBT;
            case PaymentMethodTypes.PaysafePaypal:
                return AnalyticsEvent.DEPOSIT_METHOD_PAYPAL;
            case PaymentMethodTypes.PaysafeVenmo:
                return AnalyticsEvent.DEPOSIT_METHOD_VENMO;
            case PaymentMethodTypes.FantasyMoney:
                return AnalyticsEvent.DEPOSIT_METHOD_FANTASY_MONEY;
            case PaymentMethodTypes.SocialWallet:
                return AnalyticsEvent.DEPOSIT_METHOD_SOCIAL_WALLET;
        }
    };

    const handleSelect = (method: PaymentMethod, preferredPaymentMethod?: PreferredPayment) => {
        const event = getAnalyticsEvent(method);
        if (event) {
            BetrAnalytics.trackProductEvent(event);
        }

        setSelectedPaymentMethod(method);
        setSelectedPreferredMethod(preferredPaymentMethod);
    };

    const quickDepositValues = settings?.QuickDepositButtonsFantasy;
    const handleOnSkip = () => {
        BetrAnalytics.trackProductEvent(AnalyticsEvent.FTD_SKIP);
        useProductTransitionStore.getState().actions.triggerTransition(product);
    };

    const pbToolbar = isEditing ? 0 : safeAreaInsetBottom + standardValue;
    const showQuickButtons = !!quickDepositValues && isEditing;
    const bannerMessage = shouldShowFTDBanner
        ? promoCodeDetail ?? `${ftdSetting?.ftd_banner_heading} ${ftdSetting?.ftd_banner_body}`
        : promotionToActivate?.deposit_page_banner_text ?? '';

    // pushed on top of another deposit stack screen (the coin packages), so it shows a back arrow
    const closeIconMode = isOnboarding ? 'none' : isFirstScreenInStack ? 'close' : 'back';

    const rightIcon = isOnboarding ? (
        <TouchableOpacity onPress={handleOnSkip}>
            <Text variant="titleLarge">{t('common:skip')}</Text>
        </TouchableOpacity>
    ) : null;

    if (isDepositLimitsLoading) {
        return (
            <View style={[common.flex, styles.loaderContainer, common.justifyCenter]}>
                <LoadingSpinner />
            </View>
        );
    }

    const navBarProps = isSharedSocialWallet
        ? {
              height: 48,
              titleVariant: 'titleMedium2' as const,
              title: t('wallet:coin_purchase'),
          }
        : {
              title: t('wallet:deposit_funds'),
          };

    return (
        <Screen edges={['bottom', 'top']}>
            <ScreenNavBar
                {...navBarProps}
                rightIcon={rightIcon}
                closeIconMode={closeIconMode}
                handlePressTracking={() => {
                    const event = isEditing ? AnalyticsEvent.DEPOSIT_ABORT : AnalyticsEvent.DEPOSIT_METHOD_ABORT;
                    BetrAnalytics.trackProductEvent(event);
                }}
            />
            <ScrollView contentContainerStyle={[common.grow, common.justifyCenter]}>
                <MaxWidthWrapper>
                    {isSharedSocialWallet && !!coinPackage ? (
                        <Box px="s16" pt="s20" pb="s24" justifyContent="center" alignItems="center">
                            <PurchaseSummaryCard coinPackage={coinPackage} />
                        </Box>
                    ) : (
                        <DepositNumberSection
                            {...{
                                isEditing,
                                setIsEditing,
                                errorMessage,
                                displayValue,
                            }}
                            quickDepositValuesComponent={
                                showQuickButtons ? (
                                    <QuickDepositButtonsSection
                                        selectedValue={number.toString()}
                                        setInputValue={setInputValue}
                                        quickDepositValues={quickDepositValues}
                                    />
                                ) : null
                            }
                        />
                    )}

                    {!isEditing ? (
                        <ScrollView>
                            <PaymentMethods
                                {...{
                                    handleSelect,
                                    selectedAmount: number.toString(),
                                    coinPackageId: coinPackage?.coin_package_id.toString() || '',
                                    selectedPaymentMethod,
                                    selectedPreferredMethod,
                                }}
                            />
                        </ScrollView>
                    ) : null}
                    {!isEditing && selectedPreferredMethod?.is_expired ? (
                        <Box padding="s16">
                            <Text
                                color={ds.colorToken.text.secondary}
                                variant="bodySmall"
                                textDecorationLine="underline"
                                onPress={() => {
                                    navigation.navigate('ModalWebView', {
                                        uri: URLS.HOW_TO_UPDATE_DEBIT_CARD_URL,
                                    });
                                }}
                            >
                                {t('wallet:how_do_i_update_card')}
                            </Text>
                        </Box>
                    ) : null}
                </MaxWidthWrapper>
            </ScrollView>
            <MaxWidthWrapper>
                {isSharedSocialWallet ? null : (
                    <NumberPad
                        relativePosition={isEditing}
                        isEditing={isEditing}
                        customTransitionDelay={200}
                        showKeyboard={Platform.OS === 'android' ? isEditing : true}
                        toolbar={
                            errorMessage || isEditing ? (
                                <Box style={{ paddingBottom: pbToolbar }}>
                                    <ErrorMessagePayment errorMessage={errorMessage} />
                                    <PromotionalBanner isVisible={showPromotionalBanner} message={bannerMessage} />
                                </Box>
                            ) : undefined
                        }
                        hasBottomSafeArea={false}
                        onLayout={onLayout}
                        height={numPadHeight}
                        {...{ onNumberPress, onDecimalPress, onBackspacePress }}
                    />
                )}

                <DepositButton
                    {...{
                        isEditing,
                        setIsEditing,
                        errorMessage,
                        hasMadeFirstDeposit,
                        selectedAmount: number.toString(),
                        selectedPaymentMethod,
                        selectedPreferredMethod,
                        coinPackageId: coinPackage?.coin_package_id.toString() || '',
                    }}
                />
            </MaxWidthWrapper>
        </Screen>
    );
};
